import { useState, useEffect, useRef, useCallback } from "react";

interface UseInactivityTimerProps {
  readonly timeoutMinutes: number; // Total timeout in minutes
  readonly warningMinutes: number; // Minutes before timeout to show warning
  readonly onWarning?: () => void;
  readonly onTimeout?: () => void;
  readonly enabled?: boolean;
}

interface UseInactivityTimerReturn {
  readonly isWarningActive: boolean;
  readonly timeRemainingMinutes: number;
  readonly timeRemainingSeconds: number;
  readonly resetTimer: () => void;
}

export function useInactivityTimer({
  timeoutMinutes,
  warningMinutes,
  onWarning,
  onTimeout,
  enabled = true,
}: UseInactivityTimerProps): UseInactivityTimerReturn { 

  const [isWarningActive, setIsWarningActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const countdownIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const startTimeRef = useRef<number>(Date.now());
  const warningTriggeredRef = useRef<boolean>(false);

  // Memoize calculated values to prevent recalculation on every render
  const timeoutMs = useRef(timeoutMinutes * 60 * 1000);
  const warningMs = useRef(warningMinutes * 60 * 1000);

  // Update refs when props change
  useEffect(() => {
    timeoutMs.current = timeoutMinutes * 60 * 1000;
    warningMs.current = warningMinutes * 60 * 1000;
  }, [timeoutMinutes, warningMinutes]);

  const cleanup = useCallback(() => {
    // This function cleans up all timers when component unmounts or timer is reset
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  // Use ref to store latest onTimeout to avoid recreating callbacks
  const onTimeoutRef = useRef(onTimeout);
  const onWarningRef = useRef(onWarning);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
    onWarningRef.current = onWarning;
  }, [onTimeout, onWarning]);

  const updateCountdown = useCallback(() => {
    const now = Date.now();
    const elapsed = now - startTimeRef.current;
    const remaining = timeoutMs.current - elapsed;

    if (remaining <= 0) {
      onTimeoutRef.current?.();
      setTimeRemaining(0);
      return;
    }

    // Check if we should trigger warning (5 minutes before timeout)
    if (remaining <= warningMs.current && !warningTriggeredRef.current) {
      warningTriggeredRef.current = true;
      setIsWarningActive(true);
      onWarningRef.current?.();
    }

    setTimeRemaining(remaining);
  }, []);

  const resetTimer = useCallback(() => {
    // This function resets the inactivity timer and clears any active warnings
    if (!enabled) return;

    cleanup();
    setIsWarningActive(false);
    warningTriggeredRef.current = false;
    startTimeRef.current = Date.now();
    setTimeRemaining(timeoutMs.current);

    // Start countdown immediately with 1s interval for UI updates
    countdownIntervalRef.current = setInterval(updateCountdown, 1000);
  }, [enabled, cleanup, updateCountdown]);

  useEffect(() => {
    // This effect sets up event listeners for user activity detection
    // Monitors mouse, keyboard, touch, and scroll events to detect user presence
    if (!enabled) return;
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "touchmove",
      "click",
      "keydown",
    ];

    const handleActivity = () => {
      // Simple debouncing - clear existing debounce timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Set a new debounce timeout
      debounceRef.current = setTimeout(() => {
        resetTimer();
        debounceRef.current = undefined;
      }, 100); // Very short debounce just to prevent excessive calls
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    // Initialize timer immediately
    resetTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      cleanup();
    };
  }, [enabled, resetTimer, cleanup]);
 
  const timeRemainingMinutes = Math.floor(timeRemaining / (60 * 1000));
  const timeRemainingSeconds = Math.floor((timeRemaining % (60 * 1000)) / 1000);
  return {
    isWarningActive,
    timeRemainingMinutes,
    timeRemainingSeconds,
    resetTimer,
  };
}
